import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  Typography,
  Tooltip,
  Link as MUILink,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import GitHubIcon from '@mui/icons-material/GitHub';
import HomeIcon from '@mui/icons-material/Home';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import {
  tagComponentMap,
  LLVM,
  Bevy,
  Simulation,
  Genetics,
  type TagChipProps,
  FFI,
} from './Tags';
import GithubContributions from './GithubContributions';
import get from '../api';
import axios from 'axios';

type TagComponent = React.ComponentType<TagChipProps>;

interface ProjectInfo {
  starsCount: number;
  forksCount: number;
  description: string;
  homepage: string;
  language: string;
  license: string;
}

interface LanguagePercentage {
  language: string;
  percent: number;
}

const LANG_COLORS: Record<string, string> = {
  Rust: '#DEA584',
  Python: '#3572A5',
  WGSL: '#8A2BE2',
  Others: '#9AA4B2',
};

const alphaFactor = 0.5;
const subdued = (hex: string) => alpha(hex, alphaFactor);

const wgslGradientDim = `linear-gradient(90deg, rgba(167,139,250,${alphaFactor}) 0%, rgba(96,165,250,${alphaFactor}) 100%)`;
const wgslGradientFull =
  'linear-gradient(90deg, rgba(167,139,250,1) 0%, rgba(96,165,250,1) 100%)';

function useProjectCardLogic(github: string) {
  const [info, setInfo] = useState<ProjectInfo>({
    starsCount: 0,
    forksCount: 0,
    description: 'Loading...',
    homepage: '',
    language: 'unknown',
    license: 'unknown',
  });

  const [languages, setLanguages] = useState<LanguagePercentage[]>([]);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  const repoUrl = `https://github.com/${github}`;

  useEffect(() => {
    get(`https://api.github.com/repos/${github}`)
      .then(({ data }) => {
        setInfo({
          starsCount: data.stargazers_count,
          forksCount: data.forks_count,
          description: data.description,
          homepage: data.homepage,
          language: data.language ?? 'unknown',
          license: data.license ? data.license.spdx_id : 'no',
        });
        setErrorCode(null);
      })
      .catch((err) => {
        const status = axios.isAxiosError(err)
          ? err.response?.status
          : undefined;
        setErrorCode(status ?? null);
      });
  }, [github]);

  useEffect(() => {
    get(`https://api.github.com/repos/${github}/languages`)
      .then(({ data }) => {
        const entries = Object.entries(data as Record<string, number>);
        const total = entries.reduce((s, [, n]) => s + n, 0);
        if (!total) {
          setLanguages([]);
          return;
        }

        const percents = entries.map(([language, count]) => ({
          language,
          percent: Math.round((count / total) * 100),
        }));

        const important = percents.filter((p) => p.percent >= 10);
        const othersTotal = percents
          .filter((p) => p.percent < 10)
          .reduce((s, p) => s + p.percent, 0);

        const finalData = [...important];
        if (othersTotal > 0)
          finalData.push({ language: 'Others', percent: othersTotal });
        setLanguages(finalData);
      })
      .catch(() => {});
  }, [github]);

  const isUnknownLanguage = (v?: string) =>
    (v ?? '').toLowerCase() === 'unknown';

  const langTokens = useMemo<LanguagePercentage[]>(() => {
    if (languages.length) return languages;
    if (info.language && !isUnknownLanguage(info.language))
      return [{ language: info.language, percent: 100 }];
    return [];
  }, [languages, info.language]);

  const visibleLangTokens = langTokens.filter(
    (l) => l.language.toLowerCase() !== 'unknown',
  );
  const showLanguages = visibleLangTokens.length > 0;

  return {
    info,
    errorCode,
    repoUrl,
    visibleLangTokens,
    showLanguages,
  };
}

const LangBar: React.FC<{ data: LanguagePercentage[] }> = ({ data }) => {
  if (!data.length) return null;

  return (
    <Box
      sx={{
        height: 5,
        width: '100%',
        borderRadius: 8,
        overflow: 'hidden',
        bgcolor: 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
        {data.map((l, i) => {
          const isWGSL = l.language === 'WGSL';
          const baseColor = LANG_COLORS[l.language] ?? LANG_COLORS.Others;
          const dimBackground = isWGSL ? wgslGradientDim : subdued(baseColor);
          const fullBackground = isWGSL ? wgslGradientFull : baseColor;

          return (
            <Box
              key={`${l.language}-${i}`}
              title={`${l.language}: ${l.percent}%`}
              sx={{
                width: `${Math.max(2, l.percent)}%`,
                height: '100%',
                transition: 'background 140ms ease',
                background: dimBackground,
                '&:hover': { background: fullBackground },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

function LargeProjectCard({
  title,
  github,
  customDescription,
  tags,
}: {
  title: string;
  github: string;
  customDescription?: React.ReactElement;
  tags?: TagComponent[];
}) {
  const { info, errorCode, repoUrl, visibleLangTokens, showLanguages } =
    useProjectCardLogic(github);
  const isUnknownLarge = (v?: string) => {
    const lowered = (v ?? '').toLowerCase();
    return lowered === 'unknown' || lowered === 'no';
  };
  const showLicenseChip = info.license && !isUnknownLarge(info.license);

  return (
    <Card
      sx={{
        mb: 3,
        px: 3,
        py: 2,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <MUILink
          href={repoUrl}
          underline="hover"
          color="inherit"
          sx={{ fontWeight: 600 }}
          variant="h6"
          target="_blank"
          rel="noopener noreferrer"
        >
          {title}
        </MUILink>

        <Box
          sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}
        >
          {info.starsCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
              }}
            >
              <StarOutlineIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {info.starsCount.toLocaleString()}
              </Typography>
            </Box>
          )}
          {info.forksCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
              }}
            >
              <ForkRightIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {info.forksCount.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {errorCode !== null ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 2,
          }}
        >
          Can't access GitHub API
          {typeof errorCode === 'number' ? ` (${errorCode} code)` : ''} to fetch
          project data. Read project details directly on{' '}
          <MUILink href={repoUrl} target="_blank" rel="noopener noreferrer">
            GitHub repository
          </MUILink>
          .
        </Typography>
      ) : (
        <>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{
              mb: 2,
            }}
          >
            {info.description}.
          </Typography>
          {customDescription && (
            <Box sx={{ mt: 0, mb: 2 }}>{customDescription}</Box>
          )}
        </>
      )}

      {showLanguages && <LangBar data={visibleLangTokens} />}

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
          {visibleLangTokens.map((item, idx) => {
            const Component = tagComponentMap[item.language];
            const titleAttr = `${item.language}: ${item.percent}%`;
            return Component ? (
              <Tooltip key={`${item.language}-${idx}`} title={titleAttr}>
                <Box component="span">
                  <Component dim />
                </Box>
              </Tooltip>
            ) : null;
          })}

          {tags?.map((Tag, idx) => {
            return (
              <Box key={idx} component="span">
                <Tag />
              </Box>
            );
          })}

          {showLicenseChip && (
            <Chip
              size="small"
              variant="outlined"
              label={info.license}
              sx={{ ml: 0.5 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2}}>
          <Button
            startIcon={<GitHubIcon />}
            size="small"
            sx={{ textAlign: 'right'}}
            href={repoUrl}
            target="_blank"
          >
            View Source
          </Button>
          {info.homepage && (
            <Button
              startIcon={<HomeIcon />}
              size="small"
              sx={{ textAlign: 'right' }}
              href={info.homepage}
              target="_blank"
            >
              Homepage
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  );
}

function SmallProjectRecord({
  title,
  github,
}: {
  title: string;
  github: string;
}) {
  const { info, errorCode, repoUrl, visibleLangTokens, showLanguages } =
    useProjectCardLogic(github);
  const isUnknownSmall = (v?: string) => (v ?? '').toLowerCase() === 'unknown';
  const showLicenseChip = info.license && !isUnknownSmall(info.license);

  return (
    <Box sx={{ pt: 2, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <MUILink
          href={repoUrl}
          underline="hover"
          color="inherit"
          sx={{ fontWeight: 600 }}
          variant="h6"
          target="_blank"
          rel="noopener noreferrer"
        >
          {title}
        </MUILink>

        {showLicenseChip && (
          <Chip
            size="small"
            variant="outlined"
            label={info.license === 'no' ? 'no license' : info.license}
            sx={{ ml: 0.5 }}
          />
        )}

        <Box
          sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}
        >
          {info.starsCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
              }}
            >
              <StarOutlineIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {info.starsCount.toLocaleString()}
              </Typography>
            </Box>
          )}
          {info.forksCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
              }}
            >
              <ForkRightIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {info.forksCount.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {errorCode !== null ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          Can't access GitHub API
          {typeof errorCode === 'number' ? ` (${errorCode} code)` : ''} to fetch
          project data. Read project details directly on{' '}
          <MUILink href={repoUrl} target="_blank" rel="noopener noreferrer">
            GitHub repository
          </MUILink>
          .
        </Typography>
      ) : (
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          {info.description}
        </Typography>
      )}

      {showLanguages && <LangBar data={visibleLangTokens} />}
    </Box>
  );
}

function OtherProjects() {
  return (
    <Card
      sx={{
        mb: 3,
        px: 3,
        pt: 2,
        pb: 0,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Other Open Source Projects
      </Typography>
      <Divider />
      <Box>
        <SmallProjectRecord
          title="telegram-text"
          github="SKY-ALIN/telegram-text"
        />
        <Divider />
        <SmallProjectRecord
          title="bevy-shaders"
          github="SKY-ALIN/bevy-shaders"
        />
        <Divider />
        <SmallProjectRecord title="regta" github="SKY-ALIN/regta" />
        <Divider />
        <SmallProjectRecord
          title="regta-period"
          github="SKY-ALIN/regta-period"
        />
        <Divider />
        <SmallProjectRecord
          title="space-drive-game"
          github="SKY-ALIN/space-drive-game"
        />
        <Divider>
          <Typography color="text.secondary">Public Contribution Graph</Typography>
        </Divider>
        <Box sx={{ py: 1.5 }}>
          <GithubContributions />
        </Box>
      </Box>
    </Card>
  );
}

function OpenSourceProjects() {
  return (
    <Box>
      <LargeProjectCard
        title="plasm"
        github="plasm-lang/plasm"
        tags={[LLVM]}
        customDescription={
          <Typography
            sx={{ my: 2 }}
          >
            Experimental, compiled, strongly-typed, functional, system
            programming language that inherits memory regions and algebraic
            effects ideas, focused on dev experience, extensibility and
            reliability.
          </Typography>
        }
      />
      <LargeProjectCard
        title="bevy-space-physics"
        github="SKY-ALIN/bevy-space-physics"
        tags={[Bevy, Simulation]}
        customDescription={
          <>
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Box
                component="iframe"
                src="https://www.youtube.com/embed/ltJrM44knqI"
                title="bevy-space-physics demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  border: 0,
                  display: 'block',
                }}
              />
            </Box>
            <Typography sx={{ my: 2 }}>
              The physics engine takes into account different gravity points,
              translational and angular motion and acceleration, gizmos visualization.
            </Typography>
          </>
        }
      />
      <LargeProjectCard
        title="bioforma"
        github="SKY-ALIN/bioforma"
        tags={[Genetics, FFI]}
        customDescription={
          <Typography sx={{ my: 2 }}>
            A PyO3-based bridge bringing the raw performance of the rust-bio ecosystem to Python.
            It features highly optimized tools for DNA sequence analysis, including Open Reading Frame (ORF) finders,
            generalized Smith-Waterman pairwise alignment, and lexicographical rank transformations.
            </Typography>
        }
      />

      <OtherProjects />
    </Box>
  );
}

export default OpenSourceProjects;
